import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Package, Zap, Facebook, ExternalLink, Calendar } from "lucide-react";
import { api, LOGOS, CATEGORY_IMAGES, productImage } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const FACEBOOK_EVENTS_URL = "https://www.facebook.com/DugoutCollectibles/events";

// Rotating gradient tints used to keep the category grid visually varied
// even when the categories themselves come dynamically from Square.
const CATEGORY_TINTS = [
  "from-cyan-400/30 to-brand-teal/20",
  "from-brand-navy/40 to-brand-teal/20",
  "from-brand-teal/30 to-cyan-300/20",
  "from-brand-navy/40 to-slate-400/20",
  "from-cyan-300/30 to-brand-teal/25",
  "from-brand-teal/30 to-cyan-400/25",
  "from-brand-navy/40 to-brand-teal/25",
  "from-brand-teal/25 to-cyan-300/25",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [releases, setReleases] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/new-releases").then((r) => setReleases(r.data.slice(0, 6))).catch(() => {});
    api
      .get("/categories")
      .then((r) => setCategories(r.data.slice(0, 8)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="hero-grid relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <span className="chip bg-brand-teal/15 text-brand-teal">
                <Sparkles size={12} /> Trading Cards · Tabletop · Community
              </span>
              <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl">
                Elevate <span className="text-brand-teal">your game</span>.<br />
                Booster to battlefield.
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-600">
                Your local hub for Pokémon, Magic: The Gathering, Warhammer, board games,
                dice, and every accessory in between — synced live from our in-store shelves.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
                  data-testid="hero-shop-cta"
                >
                  Shop the Store <ArrowRight size={16} />
                </Link>
                <a
                  href={FACEBOOK_EVENTS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wide text-brand-navy transition hover:bg-brand-navy hover:text-white"
                  data-testid="hero-events-cta"
                >
                  <Facebook size={16} /> This Week&apos;s Events
                </a>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-6 border-t border-slate-200 pt-6">
                <Stat label="Products in stock" value="1,500+" />
                <Stat label="Weekly events" value="10+" />
                <Stat label="Community since" value="2018" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative lg:col-span-5"
            >
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-brand-teal/20 via-cyan-300/20 to-brand-navy/10 blur-2xl" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border-4 border-white bg-white shadow-2xl">
                <img
                  src={CATEGORY_IMAGES.pokemon}
                  alt="Pokemon trading cards"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-navy-dark/90 to-transparent p-6 text-white">
                  <span className="chip bg-brand-teal text-white">New Release</span>
                  <h3 className="mt-3 font-display text-2xl font-bold">Pokémon TCG · Latest Set</h3>
                  <p className="text-sm text-white/80">In-stock now — booster boxes, ETBs, and singles.</p>
                </div>
                <img
                  src={LOGOS.shield}
                  alt="EGC"
                  className="absolute right-4 top-4 h-14 w-14 rounded-xl bg-white/95 p-1 shadow-lg"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="home-categories">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <span className="chip bg-brand-navy/10 text-brand-navy">Shop by Category</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Find your next favorite game
            </h2>
          </div>
          <Link
            to="/shop"
            className="hidden text-sm font-bold text-brand-teal hover:text-brand-navy sm:inline-flex sm:items-center sm:gap-1"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {categories.map((c, idx) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-brand-teal hover:shadow-lg"
              data-testid={`category-tile-${c.slug}`}
            >
              <img
                src={CATEGORY_IMAGES[c.slug] || CATEGORY_IMAGES.accessories}
                alt={c.name}
                className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-110"
              />
              <div className={`absolute inset-0 bg-gradient-to-tr ${CATEGORY_TINTS[idx % CATEGORY_TINTS.length]}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-dark/85 via-brand-navy/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="font-display text-lg font-bold text-white">{c.name}</h3>
                <p className="mt-0.5 text-xs font-semibold text-brand-teal">
                  {c.count} product{c.count === 1 ? "" : "s"} →
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEW RELEASES */}
      <section className="bg-white py-16 lg:py-20" data-testid="home-releases">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <span className="chip bg-brand-teal/15 text-brand-teal">
                <Zap size={12} /> Just Landed
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
                New releases & pre-orders
              </h2>
            </div>
            <Link
              to="/new-releases"
              className="text-sm font-bold text-brand-teal hover:text-brand-navy"
            >
              See all →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {releases.map((p) => (
              <ProductCard key={p.id} product={p} testIdPrefix="release" />
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS ON FACEBOOK STRIP */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-testid="home-events">
        <div className="grid items-center gap-8 rounded-3xl border-2 border-purple-500/20 bg-gradient-to-br from-purple-50 via-white to-brand-teal/5 p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <span className="chip bg-purple-500/10 text-purple-600">
              <Facebook size={12} /> Community events
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Follow our Facebook for every event
            </h2>
            <p className="mt-4 text-slate-600">
              FNM, Pokémon League, board game nights, prereleases, paint & play — we post every
              event to Facebook so you can RSVP, get reminders, and see who else is coming.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={FACEBOOK_EVENTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-fb-events-cta"
                className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide"
              >
                <Facebook size={16} /> See events on Facebook <ExternalLink size={14} />
              </a>
              <Link
                to="/events"
                className="inline-flex items-center gap-2 rounded-full border-2 border-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wide text-brand-navy transition hover:bg-brand-navy hover:text-white"
              >
                <Calendar size={16} /> What we host
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <img src={CATEGORY_IMAGES.mtg} className="aspect-square rounded-2xl object-cover" alt="MTG" />
            <img src={CATEGORY_IMAGES["board-games"]} className="mt-6 aspect-square rounded-2xl object-cover" alt="Board games" />
            <img src={CATEGORY_IMAGES.warhammer} className="aspect-square rounded-2xl object-cover" alt="Warhammer" />
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20" data-testid="home-featured">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="chip bg-brand-navy/10 text-brand-navy">
              <Package size={12} /> Staff Picks
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl">
              Featured this week
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {featured.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} testIdPrefix="featured" />
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY BANNER */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8" data-testid="home-community">
        <div className="relative overflow-hidden rounded-3xl bg-brand-navy-dark p-8 sm:p-12">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-teal/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />
          <div className="relative grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="chip bg-brand-teal/20 text-brand-teal">Come play with us</span>
              <h3 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">
                200+ board games. 20+ tables. Every night.
              </h3>
              <p className="mt-4 max-w-md text-white/75">
                Free open play, weekly tournaments, and rotating community events.
                Bring your deck, meet your next arch-nemesis, and leave with new friends.
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-brand-navy transition hover:bg-brand-teal hover:text-white"
              >
                About Elevation <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <img
                src={CATEGORY_IMAGES["board-games"]}
                className="aspect-square rounded-2xl object-cover"
                alt="Board games"
              />
              <img
                src={productImage("warhammer")}
                className="mt-8 aspect-square rounded-2xl object-cover"
                alt="Warhammer"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-display text-2xl font-extrabold text-brand-navy">{value}</div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
    </div>
  );
}
